import { useMemo } from "react"
import type {
  PermitWorkflowStageTimeline,
  PermitWorkflowTimeline,
} from "modules/permit"

export type WorkflowMetadataState = {
  workflowPermitId?: string
  workflowCurrentStageId?: string
  workflowCurrentStageCode?: string
  workflowCurrentStageSequence?: number
}

export type WorkflowMetadata = WorkflowMetadataState | null

interface UseActiveWorkflowStageParams {
  timeline?: PermitWorkflowTimeline | null
  approvalStateId?: string | null
  currentUser?: Record<string, any> | null
}

const isGuidValue = (value: string | null | undefined): boolean => {
  if (!value) return false
  return /^[0-9a-fA-F]{8}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{12}$/.test(value)
}

const normalizeString = (value: unknown): string | null => {
  if (typeof value !== "string") {
    return null
  }
  const trimmed = value.trim()
  return trimmed.length > 0 ? trimmed : null
}

const getNormalizedUserId = (user?: Record<string, any> | null): string | null => {
  if (!user) {
    return null
  }
  const candidates: Array<string | null> = [
    normalizeString(user.id),
    normalizeString(user.user_id),
    normalizeString(user.userId),
    normalizeString(user.emp_id),
    normalizeString(user.empId),
    normalizeString(user.employee_id),
    normalizeString(user.employeeId),
  ]

  let fallback: string | null = null

  for (const value of candidates) {
    if (!value) continue
    const normalized = value.toLowerCase()
    if (isGuidValue(value)) {
      return normalized
    }
    if (!fallback) {
      fallback = normalized
    }
  }

  return fallback
}

const collectNormalizedRoleIds = (user?: Record<string, any> | null): Set<string> => {
  const normalizedRoles = new Set<string>()
  if (!user) {
    return normalizedRoles
  }

  const addRole = (value: unknown) => {
    const normalized = normalizeString(value)
    if (!normalized) return
    normalizedRoles.add(normalized.toLowerCase())
  }

  addRole(user.role_id)
  addRole(user.roleId)
  addRole(user.role?.id)

  const userRoles =
    (Array.isArray(user.roles) && user.roles) ||
    (Array.isArray(user.user_roles) && user.user_roles) ||
    []

  for (const role of userRoles as Array<any>) {
    if (!role) continue
    addRole(role.roleId ?? role.role_id ?? role.id)
  }

  return normalizedRoles
}

const findUserStage = (
  stages: PermitWorkflowStageTimeline[],
  currentUser?: Record<string, any> | null,
): PermitWorkflowStageTimeline | null => {
  if (!currentUser) {
    return null
  }
  const userId = getNormalizedUserId(currentUser)
  const roleIds = collectNormalizedRoleIds(currentUser)

  return (
    stages.find((stage) => {
      const status = (stage.status ?? "").toUpperCase()
      if (status !== "IN_PROGRESS") {
        return false
      }

      const assignedUserId =
        typeof stage.assignedUserId === "string" ? stage.assignedUserId.trim().toLowerCase() : null
      if (assignedUserId) {
        return Boolean(userId) && assignedUserId === userId
      }

      const assignedRoleId =
        typeof stage.assignedRoleId === "string" ? stage.assignedRoleId.trim().toLowerCase() : null
      if (assignedRoleId) {
        return roleIds.has(assignedRoleId)
      }

      return false
    }) ?? null
  )
}

export const useActiveWorkflowStage = ({
  timeline,
  approvalStateId,
  currentUser,
}: UseActiveWorkflowStageParams): PermitWorkflowStageTimeline | null =>
  useMemo(() => {
    if (!timeline?.stages?.length) {
      return null
    }

    const userStage = findUserStage(timeline.stages, currentUser)
    if (userStage) {
      return userStage
    }

    const inProgressStage =
      timeline.stages.find((stage) => (stage.status ?? "").toUpperCase() === "IN_PROGRESS") ?? null
    if (inProgressStage) {
      return inProgressStage
    }

    if (approvalStateId) {
      return timeline.stages.find((stage) => stage.stageId === approvalStateId) ?? null
    }

    return null
  }, [timeline, approvalStateId, currentUser])

interface UseWorkflowMetadataParams {
  mode: "create" | "edit"
  permitId?: string | null
  activeStage?: PermitWorkflowStageTimeline | null
  approvalStateId?: string | null
}

export const useWorkflowMetadata = ({
  mode,
  permitId,
  activeStage,
  approvalStateId,
}: UseWorkflowMetadataParams): WorkflowMetadata =>
  useMemo(() => {
    if (mode !== "edit" || !permitId) {
      return null
    }

    const metadata: WorkflowMetadataState = {
      workflowPermitId: permitId,
    }

    const resolvedStageId = activeStage?.stageId ?? approvalStateId ?? undefined
    if (resolvedStageId) {
      metadata.workflowCurrentStageId = resolvedStageId
    }

    if (activeStage?.code) {
      metadata.workflowCurrentStageCode = activeStage.code
    }

    if (typeof activeStage?.seqIndex === "number") {
      metadata.workflowCurrentStageSequence = activeStage.seqIndex
    }

    return metadata
  }, [mode, permitId, activeStage, approvalStateId])
